import { Tabs } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useCurrentUser } from '../../../shared/features/session/components/CurrentUserProvider';
import { DetailPageSkeleton } from '../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { FollowUpsTab } from '../features/follow-ups/public/FollowUpsTab';
import { PaymentPromisesTab } from '../features/payment-promises/public/PaymentPromisesTab';
import { CustomerContacts } from '../components/CustomerContacts';
import { CustomerDetailsForm } from '../components/CustomerDetailsForm';
import { CustomerRestrictionsTab } from '../components/CustomerRestrictionsTab';
import { CustomerHistoryTab } from '../components/CustomerHistoryTab';
import { CUSTOMER_TAB_LABELS, CUSTOMER_TABS, customerTabPath, isFinanceTab, type CustomerTab } from '../customers.paths';
import { CustomerStatusControl } from '../components/CustomerStatusControl';
import { useCustomerDetail } from '../hooks/use-customer-detail';
import { withinSection } from '../../../shared/components/section-trail';

/**
 * A customer's own page: fields and contacts on one tab, history on the other. The
 * first page where nav access (`customer.view`) and mutate access (`customer.manage`)
 * diverge for an existing role, so every edit control here is hidden or disabled for
 * a `customer.view`-only visitor — the server remains the authority either way.
 */
export function CustomerDetailPage({ tab }: { tab: CustomerTab }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { state, updateError, saving, update, activate, deactivate, addContact, updateContact, removeContact } = useCustomerDetail(id);
  const { state: currentUser } = useCurrentUser();
  const permissions = currentUser.status === 'authenticated' ? currentUser.user.permissions : [];
  const canManage = permissions.includes('customer.manage');
  const canSeeFinance = permissions.includes('billing.view');
  const canRecordFollowUps = permissions.includes('collection.record');

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  if (state.status === 'loading') return <DetailPageSkeleton label="Loading customer" breadcrumbs={withinSection('customers')} tabs={CUSTOMER_TABS.length} />;

  if (state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Customer not found" description="This customer doesn't exist, or the link is out of date." />;
  }

  if (state.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load this customer" description="Something went wrong loading this customer. Try reloading the page." />
    );
  }

  if (isFinanceTab(tab) && !canSeeFinance) return <Navigate to={customerTabPath(state.customer.id, 'details')} replace />;

  const { customer } = state;

  return (
    <>
      <PageHeader
        breadcrumbs={withinSection('customers')}
        title={customer.name}
        description={customer.kraPin}
        actions={<CustomerStatusControl customer={customer} canManage={canManage} onActivate={activate} onDeactivate={deactivate} />}
      />
      <Tabs
        activeKey={tab}
        onChange={(key) => navigate(customerTabPath(customer.id, key as CustomerTab))}
        items={[
          {
            key: 'details',
            label: CUSTOMER_TAB_LABELS.details,
            children: (
              <div className="flex flex-col gap-8">
                <CustomerDetailsForm customer={customer} canManage={canManage} error={updateError} saving={saving} onSave={update} />
                <CustomerContacts contacts={customer.contacts} canManage={canManage} onAdd={addContact} onUpdate={updateContact} onRemove={removeContact} />
              </div>
            ),
          },
          {
            key: 'restrictions',
            label: CUSTOMER_TAB_LABELS.restrictions,
            children: <CustomerRestrictionsTab customerId={customer.id} canManage={canManage} />,
          },
          ...(canSeeFinance ? financeTabs(customer.id, canRecordFollowUps) : []),
          { key: 'history', label: CUSTOMER_TAB_LABELS.history, children: <CustomerHistoryTab customerId={customer.id} /> },
        ]}
      />
    </>
  );
}

/** The tabs that need `billing.view`: the follow-up log and the promises to pay. */
function financeTabs(customerId: string, canRecord: boolean) {
  return [
    { key: 'finance', label: CUSTOMER_TAB_LABELS.finance, children: <FollowUpsTab customerId={customerId} canRecord={canRecord} /> },
    { key: 'promises', label: CUSTOMER_TAB_LABELS.promises, children: <PaymentPromisesTab customerId={customerId} canRecord={canRecord} /> },
  ];
}
